import React, { useMemo } from 'react';
import type { BBCommonProps } from 'types/story';
import { mergeDesignModeSelection, type DesignModeSelection } from 'common/design-tokens';
import { getDesignModeAttributes, useDesignModeSelection } from 'client/components/pages/Story/DesignModes/Context';
import { useCardTransitionContext, TransitionHandlerContextType } from '../../CardTransition/Context';
import { withExpose } from './WithExpose';

function withCardTransitionContext<P>(Component: React.ComponentType<P & TransitionHandlerContextType>) {
	type WithCardTransitionProps = Pick<
		BBCommonProps,
		| 'children'
		| 'currentMediaQuery'
		| 'editorMode'
		| 'getComputedComponentProps'
		| 'isCardTree'
		| 'mediaQuery'
		| 'onTransitionEnd'
		| 'symbol'
		| 'type'
		| 'uiConfig'
		| '_id'
		| 'editableModeProps'
	> & {
		designMode?: DesignModeSelection;
	};

	const WrappedComponent = withExpose(Component);

	function WithCardTransitionContext(props: Omit<P & WithCardTransitionProps, keyof TransitionHandlerContextType>) {
		const context = useCardTransitionContext();
		const inheritedDesignModeSelection = useDesignModeSelection();
		// Story and Card selections cascade through context, but a building-block override is local. Stamp the
		// complete effective selection on every building-block root so inherited CSS variables from a locally
		// overridden DOM ancestor reset when rendering its child building blocks. Do not add a Provider here:
		// a future explicit subtree-scope feature may opt into one without changing this local default.
		const designModeSelection = useMemo(
			() => mergeDesignModeSelection(inheritedDesignModeSelection, props.designMode),
			[inheritedDesignModeSelection, props.designMode]
		);
		const uiConfig = useMemo(
			() => ({
				...props.uiConfig,
				nodeProps: {
					...props.uiConfig.nodeProps,
					...getDesignModeAttributes(designModeSelection),
				},
			}),
			[designModeSelection, props.uiConfig]
		);

		return (
			<WrappedComponent
				in={context.in}
				onTransitionEnd={context.onTransitionEnd!}
				{...props}
				uiConfig={uiConfig}
				designModeSelection={designModeSelection}
			/>
		);
	}

	WithCardTransitionContext.displayName = `WithCardTransitionContext(${Component.displayName || Component.name})`;

	return WithCardTransitionContext;
}

export { withCardTransitionContext };
